import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useEffect,
} from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { Box } from '@chakra-ui/react';
import { ChartRendererProps } from '../types';

// Import required Highcharts modules
import HighchartsMore from 'highcharts/highcharts-more';
import HighchartsExporting from 'highcharts/modules/exporting';
import HighchartsExportData from 'highcharts/modules/export-data';
import HighchartsAccessibility from 'highcharts/modules/accessibility';
import HighchartsFunnel from 'highcharts/modules/funnel';
import {
  getSeriesData,
  getHighchartsType,
  getChartTypePreset,
  applyChartTypePreset,
} from '../chartUtils';
import { useKFlags } from '@/hooks/use-k-flags';

// Initialize Highcharts modules
if (typeof Highcharts === 'object') {
  HighchartsMore(Highcharts);
  HighchartsExporting(Highcharts);
  HighchartsExportData(Highcharts);
  HighchartsAccessibility(Highcharts);
  HighchartsFunnel(Highcharts);
}

export interface ChartRendererRef {
  getChart: () => Highcharts.Chart | undefined;
}

export const ChartRenderer = forwardRef<ChartRendererRef, ChartRendererProps>(
  (
    {
      data,
      chartType,
      width = '100%',
      height = '400px',
      highchartsOptions = {},
      margin: _margin = '0 auto',
      customOptions = {},
      onError: _onError,
      onDrillDownOpen = () => {},
      yAxis = '',
      isFlow = false,
      seriesName = false,
      skipDt = false,
      isPipeline = false,
      chartStyle = {},
      isStaticReport = false,
      isPrintView = false,
    },
    ref
  ) => {
    const chartRef = useRef<HighchartsReact.RefObject>(null);
    useImperativeHandle(ref, () => ({
      getChart: () => chartRef.current?.chart,
    }));
    const { feDrilldown } = useKFlags();

    useEffect(() => {
      const chart = chartRef.current?.chart;
      if (chart) {
        chart.reflow();
      }
    }, [width, height]);

    const totalCount = data?.series?.reduce((acc, curr) => {
      acc += curr?.y;
      return acc;
    }, 0);

    const options: Highcharts.Options = {
      chart: {
        type: getHighchartsType(chartType),
        animation: true,
        backgroundColor: 'rgba(0,0,0,0)',
        style: {
          fontFamily: 'inherit',
        },
        ...highchartsOptions.chart,
      },
      legend: {
        enabled:
          Array.isArray(data?.series) &&
          data?.series?.[0]?.data &&
          data?.series?.length > 1,
      },

      title: {
        text: undefined,
        ...highchartsOptions.title,
      },
      subtitle: {
        ...(chartType === 'pie'
          ? {
              useHTML: true,
              text: `<div style="text-align:center;">
								 <p style="font-size:12px;font-weight:400;margin:0;">Total</p>
								 <p style="font-size:24px;font-weight:500;color:#1D2129;margin:0;">${totalCount}</p>
							 </div>`,
              verticalAlign: 'middle',
            }
          : {}),
      },
      credits: {
        enabled: false,
      },
      series: getSeriesData(data, chartType, customOptions),
      xAxis: data?.categories ? { categories: data.categories } : undefined,
      tooltip: {
        shared: true,
        ...highchartsOptions.tooltip,
      },
      plotOptions: {
        series: {
          cursor: feDrilldown ? 'pointer' : 'default',
          point: {
            events: {
              click: feDrilldown
                ? function () {
                    const point = this as Highcharts.Point;
                    const userOpts = this.series.userOptions as Record<
                      string,
                      unknown
                    >;
                    const exactPointData = {
                      dt: !skipDt ? this.category : null,
                      g1:
                        (point as Record<string, unknown>).g1 ??
                        userOpts?.g1 ??
                        null,
                      g2:
                        (point as Record<string, unknown>).g2 ??
                        userOpts?.g2 ??
                        null,
                      metric: isFlow
                        ? this.series.name
                        : seriesName
                          ? this.name
                          : isPipeline
                            ? ((point as Record<string, unknown>)
                                .custom_id as string)
                            : yAxis,
                      yAxis: yAxis,
                      xAxis: this.name || this.category,
                      type: 'chart',
                      y: this.y,
                      flowdt: isFlow ? data?.flowdt[this.index] : null,
                      ...(chartType === 'column'
                        ? { chartValue: this.series.userOptions.name }
                        : {}),
                      metricValue: this.y,
                    };
                    onDrillDownOpen(exactPointData);
                  }
                : () => {},
            },
          },
          animation: {
            duration: 1500,
            easing: 'easeOutQuart',
          },
          ...(chartType === 'pyramid' && { center: ['40%', '50%'] }),
          states: {
            hover: {
              animation: {
                duration: 0,
              },
            },
          },
        },

        pyramid: {
          reversed: false,
          dataLabels: {
            enabled: true,
            useHTML: true,
            ...(isPrintView && {
              inside: false,
              distance: 10,
              connectorWidth: 1,
            }),
            format:
              '<div style="font-size: 10px; font-weight: 400;">{point.name} - {y}</div>',
            style: {
              textOutline: 'none',
              color: '#000',
            },
          },
          width: '80%',
        },
        area: {
          fillColor: {
            linearGradient: {
              x1: 0,
              y1: 0,
              x2: 0,
              y2: 1,
            },
            stops: [
              [0, '#0938980d'],
              [1, 'rgba(255, 255, 255, 0.2)'],
            ],
          },
          lineWidth: 2,
          lineColor: '#605AFB',
          marker: {
            enabled: false,
          },
          shadow: false,
          states: {
            hover: {
              lineWidth: 2,
            },
          },
          threshold: null,
        },
        pie: {
          borderWidth: 0,
          size: '42%',
          borderRadius: 0,
          innerSize: '80%',
          dataLabels: {
            enabled: true,
            distance: 8,
            formatter: function () {
              const percentage =
                this.percentage % 1 === 0
                  ? this.percentage.toFixed(0)
                  : this.percentage.toFixed(1);

              return `<p style="font-size:10px;font-weight:400;">${this.point.name} - </p> <p style="font-size:10px;font-weight:650;">${this.y}</p> <p style="font-size:10px;font-weight:400;">(${percentage}%)</p>`;
            },
            style: {
              color: '#000',
              fontSize: '12px',
            },
          },
        },
        column: {
          stacking: 'normal',
          dataLabels: {
            enabled: true,
            inside: true,
            align: 'center',
            verticalAlign: 'middle',
            color: '#FFFFFF',
            backgroundColor: 'none',
            borderColor: 'none',
            style: {
              color: '#000000',
              textOutline: 'none',
            },
          },
        },
        ...highchartsOptions?.plotOptions,
      },
      exporting: {
        enabled: false,
        ...highchartsOptions.exporting,
      },
      ...highchartsOptions,
    };

    const preset = getChartTypePreset(chartType);
    if (preset) {
      applyChartTypePreset(options, preset);
    }

    return (
      <Box width="100%" height="100%" {...(isStaticReport ? chartStyle : {})}>
        <HighchartsReact
          ref={chartRef}
          highcharts={Highcharts}
          options={options}
          containerProps={{ style: { height, width } }}
        />
      </Box>
    );
  }
);

ChartRenderer.displayName = 'ChartRenderer';
